import { qyooExperienceManifestSchema } from "../../src/manifest/schema";
import type { Capability, ExperienceType, Manifest } from "./types";

export const MAX_QYOO_ID = 68_719_476_735;

export function networkMismatch(walletChainId: number | null, expectedChainId: number | null): boolean {
  return walletChainId !== null && expectedChainId !== null && walletChainId !== expectedChainId;
}

export function parseQyooInput(value: string): number {
  if (!/^(0|[1-9][0-9]*)$/.test(value)) throw new Error("Enter a decimal Qyoo ID");
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0 || parsed > MAX_QYOO_ID) {
    throw new Error(`Qyoo IDs run from 0 through ${MAX_QYOO_ID.toLocaleString()}`);
  }
  return parsed;
}

export interface EditorDraft {
  title: string;
  subtitle: string;
  icon: string;
  background: string;
  foreground: string;
  type: ExperienceType;
  url: string;
  capabilities: Capability[];
  physicalWidthMeters: string;
  nativeJSON: string;
  artworkJSON: string;
}

export const emptyDraft: EditorDraft = {
  title: "My Qyoo",
  subtitle: "Tap into something new.",
  icon: "sf:sparkles",
  background: "#C7FF42",
  foreground: "#101010",
  type: "web",
  url: "https://example.com",
  capabilities: [],
  physicalWidthMeters: "",
  artworkJSON: "",
  nativeJSON: '{\n  "type": "ar",\n  "capabilities": [],\n  "content": {\n    "type": "proceduralComponent",\n    "component": "qyoo.qyoob/1",\n    "color": "#9EF56E",\n    "material": "matte",\n    "eyes": "two"\n  },\n  "anchor": {\n    "type": "qyoo",\n    "scaleMode": "relative",\n    "attachmentMode": "worldAnchored"\n  },\n  "registration": {\n    "pivot": "bottomCenter",\n    "offset": [\n      0,\n      0,\n      0\n    ]\n  },\n  "sizing": {\n    "scaleRatio": 0.75\n  },\n  "orientation": {\n    "facing": "cameraAtPlacement",\n    "yawDegrees": 0\n  },\n  "animation": {\n    "profile": "qyoobIdle",\n    "speed": 1\n  },\n  "eventBindings": []\n}'
};

export function draftFromManifest(manifest: Manifest | null): EditorDraft {
  if (!manifest) return { ...emptyDraft };
  const experience = manifest.experience;
  const url =
    experience.type === "nativeAction"
      ? ""
      : experience.type === "ar"
        ? ""
        : experience.url;
  const physicalWidthMeters = experience.type === "ar" && experience.anchor.scaleMode === "knownPhysicalWidth"
    ? String(experience.anchor.physicalWidthMeters)
    : "";
  return {
    ...manifest.presentation,
    icon: manifest.presentation.icon ?? "",
    type: experience.type,
    url,
    capabilities: experience.type === "interactiveWeb" ? [...experience.capabilities] : [],
    physicalWidthMeters,
    artworkJSON: "artwork" in manifest.presentation && manifest.presentation.artwork ? JSON.stringify(manifest.presentation.artwork, null, 2) : "",
    nativeJSON: experience.type === "ar" ? JSON.stringify(experience, null, 2) : emptyDraft.nativeJSON
  };
}

export function manifestFromDraft(qyoo: number, revision: number, draft: EditorDraft): Manifest {
  if (!draft.title.trim() || draft.title.length > 120) throw new Error("Title is required (120 characters maximum)");
  if (draft.subtitle.length > 240) throw new Error("Subtitle must be 240 characters or fewer");
  if (!/^#[0-9a-fA-F]{6}$/.test(draft.background) || !/^#[0-9a-fA-F]{6}$/.test(draft.foreground)) {
    throw new Error("Theme colors must use #RRGGBB");
  }
  if (draft.icon && !draft.icon.startsWith("sf:") && !isHttps(draft.icon)) {
    throw new Error("Icon must be an sf: symbol or HTTPS URL");
  }
  const presentation = {
    title: draft.title.trim(),
    subtitle: draft.subtitle.trim(),
    ...(draft.icon.trim() ? { icon: draft.icon.trim() } : {}),
    background: draft.background.toUpperCase(),
    foreground: draft.foreground.toUpperCase()
  };
  if (draft.type === "nativeAction") {
    return { schema: "qyoo.experience/1", qyoo, revision, presentation,
      experience: { type: "nativeAction", action: "showPayload", capabilities: [] } };
  }
  if (draft.type === "ar") {
    const candidate = { schema: "qyoo.experience/2", qyoo, revision, presentation: {...presentation, ...(draft.artworkJSON.trim() ? {artwork: JSON.parse(draft.artworkJSON)} : {})},
      runtime: {id: "qyoo.ios.native", minimumVersion: "2.0.0"},
      experience: JSON.parse(draft.nativeJSON) as unknown };
    return qyooExperienceManifestSchema.parse(candidate);
  }
  if (!isHttps(draft.url)) throw new Error("Experience destinations must be HTTPS URLs without credentials");
  if (draft.type === "interactiveWeb") {
    return { schema: "qyoo.experience/1", qyoo, revision, presentation,
      experience: { type: "interactiveWeb", url: draft.url, capabilities: [...new Set(draft.capabilities)] } };
  }
  return { schema: "qyoo.experience/1", qyoo, revision, presentation,
    experience: { type: draft.type, url: draft.url, capabilities: [] } };
}

function isHttps(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !!url.hostname && !url.username && !url.password;
  } catch {
    return false;
  }
}
